import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import DataDashboard from '../../../common/DataDashboard';
import HeaderActions from '../../../common/HeaderActions';
import { createStandardHeaderActions } from '../../../common/HeaderActions/helpers';
import { API_ENDPOINTS } from '../../../../utils/config';
import { useAuth } from '../../../../context/AuthContext';
import { getDynamicSubItems } from '../../../../utils/getDynamicSubItems';
import { getRFQNavigationInfo, getRFQPageRoute, getRFQPageName } from '../../../../utils/rfqNavigation';
import SuccessModal from '../../../SuccessModal';
import { useRfqLock } from '../../../../hooks/useRfqLock';
import RfqLockedModal from '../../../common/RfqLockedModal';
import './RFQQuotesReviewDashboard.scss';
import QuoteAttachmentsModal from '../../../admin-v2/dashboard/RFQQuotesReviewDashboard/QuoteAttachmentsModal';
import UserLayout from '../../layout/UserLayout';

interface UserRFQQuotesReviewDashboardProps {
  user: any;
  currentRoute: string;
  onNavigate: (route: string) => void;
  onLogout: () => void;
}

interface SupplierQuotesSummaryRow {
  supplier_id: string;
  supplier_readable_id: string;
  supplier_name: string;
  overall_score?: number | null;
  total_price: number;
  total_price_display?: string;
  currency: string;
  lead_time: string;
  payment_terms: string;
  delivery_cost: string;
  quote_valid_until: string;
  products?: Array<{
    product_name: string;
    quantity: number;
    unit_price: number | null;
    total_price: number | null;
    currency: string;
    has_price: boolean;
  }>;
}

interface QuotesSummaryRowView extends SupplierQuotesSummaryRow {
  id: string;
  total_price_display: string;
  overall_score_display: string;
  quote_link: string | null;
}

type EditableQuoteField = 'lead_time' | 'payment_terms' | 'delivery_cost' | 'quote_valid_until';

const EDITABLE_QUOTE_FIELDS: EditableQuoteField[] = [
  'lead_time',
  'payment_terms',
  'delivery_cost',
  'quote_valid_until'
];

const EDITABLE_FIELDS_SET = new Set<EditableQuoteField>(EDITABLE_QUOTE_FIELDS);

const formatNumber = (value: number, currency?: string) => {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return '—';
  }
  const formatted = value.toLocaleString('tr-TR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
  return currency ? `${formatted} ${currency}` : formatted;
};

const formatText = (value?: string) => {
  if (!value) return '—';
  return value;
};

const normalizeFieldValue = (value: unknown): string =>
  value === null || value === undefined ? '' : String(value);

const UserRFQQuotesReviewDashboard: React.FC<UserRFQQuotesReviewDashboardProps> = ({
  user,
  currentRoute,
  onNavigate,
  onLogout
}) => {
  const location = useLocation();
  const { token } = useAuth();

  const requestId = useMemo(() => {
    const segments = location.pathname.split('/');
    const rfqIndex = segments.indexOf('rfq'); // For /dashboard/user/rfq/ pattern
    if (rfqIndex >= 0 && segments.length > rfqIndex + 1) {
      return segments[rfqIndex + 1];
    }
    return '';
  }, [location.pathname]);

  // Permission check - redirect if no rfq_operations
  useEffect(() => {
    if (user) {
      const permissions = user.permissions;
      const hasRfqOperations =
        (Array.isArray(permissions) ? permissions.includes('rfq_operations') : permissions?.rfq_operations) ||
        user.role === 'admin' ||
        (user as any).is_admin;

      if (!hasRfqOperations) {
        onNavigate('/dashboard/user/inbox/open');
      }
    }
  }, [user, onNavigate]);

  const baseRoute = '/dashboard/user/rfq';

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [summaryRows, setSummaryRows] = useState<SupplierQuotesSummaryRow[]>([]);
  const [requestDetails, setRequestDetails] = useState<any | null>(null);
  const [selectedSupplierIds, setSelectedSupplierIds] = useState<string[]>([]);
  const [quoteSelections, setQuoteSelections] = useState<any[]>([]);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null);

  const [isEditing, setIsEditing] = useState<boolean>(false);

  // RFQ Lock Hook
  const { lockInfo: rfqLockInfo, showLockModal: showRfqLockModal, setShowLockModal: setShowRfqLockModal } = useRfqLock(requestId);
  const [showSummaryPage, setShowSummaryPage] = useState<boolean>(false);
  const [isFinishing, setIsFinishing] = useState<boolean>(false);
  const [finishError, setFinishError] = useState<string | null>(null);
  const [finishSuccess, setFinishSuccess] = useState<string | null>(null);
  const [showFinishSuccessModal, setShowFinishSuccessModal] = useState<boolean>(false);
  const initialSelectionsAppliedRef = useRef<boolean>(false);
  const pendingFieldUpdatesRef = useRef<Record<string, Partial<Record<EditableQuoteField, string>>>>({});
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const toggleRowExpanded = useCallback((supplierId: string) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(supplierId)) {
        newSet.delete(supplierId);
      } else {
        newSet.add(supplierId);
      }
      return newSet;
    });
  }, []);

  // Attachments Modal State
  const [showAttachmentsModal, setShowAttachmentsModal] = useState(false);
  const [attachmentSupplier, setAttachmentSupplier] = useState<{ id: string; name: string } | null>(null);
  const [attachmentCounts, setAttachmentCounts] = useState<Record<string, number>>({});



  const fetchQuotesSummary = useCallback(async () => {
    if (!token || !requestId) {
      setSummaryRows([]);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(API_ENDPOINTS.REQUEST_QUOTES_SUMMARY(requestId), {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to load quotes summary (status ${response.status})`);
      }

      const payload = await response.json();
      if (!payload?.success || !Array.isArray(payload.data)) {
        throw new Error('Quotes summary response is not valid');
      }

      const rows = payload.data as SupplierQuotesSummaryRow[];
      setSummaryRows(rows);
    } catch (err: any) {
      console.error('Failed to fetch quotes summary:', err);
      setError(err?.message || 'Failed to load quotes summary');
      setSummaryRows([]);
    } finally {
      setIsLoading(false);
    }
  }, [token, requestId]);

  const fetchRequestDetails = useCallback(async () => {
    if (!token || !requestId) {
      setRequestDetails(null);
      return;
    }

    try {
      const response = await fetch(API_ENDPOINTS.REQUEST_V2_DETAIL(requestId), {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to load request details (status ${response.status})`);
      }

      const payload = await response.json();
      if (!payload?.success || !payload.data) {
        throw new Error('Request details response is not valid');
      }

      setRequestDetails(payload.data);
      setQuoteSelections(Array.isArray(payload.data.quotes_selections) ? payload.data.quotes_selections : []);
    } catch (err: any) {
      console.error('Failed to fetch request details:', err);
    }
  }, [token, requestId]);

  useEffect(() => {
    fetchQuotesSummary();
  }, [fetchQuotesSummary]);

  useEffect(() => {
    fetchRequestDetails();
  }, [fetchRequestDetails]);

  // Redirect if request is in select_supplier or completed phase
  useEffect(() => {
    if (requestDetails) {
      if (requestDetails.phase === 'completed') {
        onNavigate('/dashboard/user/inbox/finalized');
      } else if (requestDetails.phase === 'select_supplier') {
        onNavigate('/dashboard/user/inbox/open');
      }
    }
  }, [requestDetails, onNavigate]);

  const existingSelectionsBySupplier = useMemo(() => {
    const map = new Map<string, any[]>();
    if (Array.isArray(requestDetails?.quotes_selections)) {
      requestDetails.quotes_selections.forEach((selection: any) => {
        const supplierId =
          selection?.supplier_id
            ? String(selection.supplier_id)
            : selection?.supplier?._id
              ? String(selection.supplier._id)
              : null;
        if (!supplierId) {
          return;
        }
        if (!map.has(supplierId)) {
          map.set(supplierId, []);
        }
        map.get(supplierId)!.push(selection);
      });
    }
    return map;
  }, [requestDetails?.quotes_selections]);

  const columns = useMemo(
    () => [
      {
        key: 'supplier_name',
        title: 'Supplier',
        type: 'text' as const,
        sortable: true
      },
      {
        key: 'products',
        title: 'Products',
        type: 'text' as const,
        sortable: false,
        render: (value: unknown, row: QuotesSummaryRowView) => {
          const products = (row as any).products || [];
          const productsWithPrice = products.filter((p: any) => p.has_price).length;
          const totalProducts = products.length;
          const isExpanded = expandedRows.has(row.supplier_id);

          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  toggleRowExpanded(row.supplier_id);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center'
                }}
                title={isExpanded ? 'Collapse' : 'Expand'}
              >
                {isExpanded ? '▼' : '▶'}
              </button>
              <span>
                {productsWithPrice}/{totalProducts} priced
              </span>
            </div>
          );
        }
      },
      {
        key: 'total_price_display',
        title: 'Total Price',
        type: 'text' as const,
        sortable: true
      },
      {
        key: 'lead_time',
        title: 'Lead Time',
        type: 'text' as const,
        sortable: false,
        editable: true,
        render: (value: string | null | undefined) => formatText(value ?? undefined)
      },
      {
        key: 'payment_terms',
        title: 'Payment Terms',
        type: 'text' as const,
        sortable: false,
        editable: true,
        render: (value: string | null | undefined) => formatText(value ?? undefined)
      },
      {
        key: 'delivery_cost',
        title: 'Delivery Cost',
        type: 'text' as const,
        sortable: false,
        editable: true,
        render: (value: string | null | undefined) => formatText(value ?? undefined)
      },
      {
        key: 'quote_valid_until',
        title: 'Quote Valid Until',
        type: 'text' as const,
        sortable: false,
        editable: true,
        render: (value: string | null | undefined) => formatText(value ?? undefined)
      },
      {
        key: 'overall_score_display',
        title: 'Overall Evaluation',
        type: 'text' as const,
        sortable: true
      },
      {
        key: 'quote_link',
        title: 'Quote',
        type: 'text' as const,
        sortable: false,
        render: (value: unknown, row: QuotesSummaryRowView) => {
          return (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setAttachmentSupplier({
                  id: row.supplier_id,
                  name: row.supplier_name
                });
                setShowAttachmentsModal(true);
              }}
              style={{
                background: 'none',
                border: 'none',
                padding: 0,
                color: '#2563eb',
                textDecoration: 'underline',
                cursor: 'pointer',
                fontFamily: 'inherit',
                fontSize: 'inherit'
              }}
            >
              {`View Attachments ${typeof attachmentCounts[row.supplier_id] === 'number' ? `(${attachmentCounts[row.supplier_id]})` : ''}`}
            </button>
          );
        }
      }
    ], [expandedRows, toggleRowExpanded, attachmentCounts]
  );

  const tableData: QuotesSummaryRowView[] = useMemo(() => {
    if (!summaryRows || summaryRows.length === 0) {
      return [];
    }

    return summaryRows.map((row) => {
      const existingSelection = existingSelectionsBySupplier.get(String(row.supplier_id))?.[0];
      const communicationUrl = requestId
        ? `/dashboard/rfqs/open/${requestId}/communication/${row.supplier_id}`
        : null;

      const totalPriceDisplay = row.total_price_display || formatNumber(Number(row.total_price || 0), row.currency);
      const overallEvaluationDisplay =
        row.overall_score === null || row.overall_score === undefined
          ? '—'
          : row.overall_score?.toFixed(2);

      return {
        ...row,
        id: row.supplier_id,
        total_price_display: totalPriceDisplay,
        overall_score_display: overallEvaluationDisplay,
        lead_time: normalizeFieldValue(existingSelection?.lead_time ?? row.lead_time),
        payment_terms: normalizeFieldValue(existingSelection?.payment_terms ?? row.payment_terms),
        delivery_cost: normalizeFieldValue(existingSelection?.delivery_cost ?? row.delivery_cost),
        quote_valid_until: normalizeFieldValue(
          existingSelection?.quote_valid_until ?? row.quote_valid_until
        ),
        quote_link: communicationUrl
      };
    });
  }, [summaryRows, requestId, existingSelectionsBySupplier]);

  // Fetch attachment counts - placed here because it depends on tableData
  useEffect(() => {
    const fetchAttachmentCounts = async () => {
      if (!requestId || !token || !tableData.length) return;

      const counts: Record<string, number> = {};
      const uniqueSupplierIds = Array.from(new Set(tableData.map(row => row.supplier_id)));

      await Promise.all(uniqueSupplierIds.map(async (supplierId) => {
        try {
          const response = await fetch(API_ENDPOINTS.REQUEST_COMMUNICATION_MESSAGES(requestId, String(supplierId)), {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          const result = await response.json();
          if (result.success && result.data?.messages) {
            let count = 0;
            result.data.messages.forEach((msg: any) => {
              if (msg.direction === 'received' && msg.attachments) {
                count += msg.attachments.length;
              }
            });
            counts[supplierId] = count;
          }
        } catch (err) {
          console.error(`Failed to fetch attachments for ${supplierId}`, err);
        }
      }));

      setAttachmentCounts(prev => ({ ...prev, ...counts }));
    };

    fetchAttachmentCounts();
  }, [requestId, token, tableData]);

  const summaryRowMap = useMemo(() => {
    const map = new Map<string, QuotesSummaryRowView>();
    tableData.forEach((row) => {
      map.set(String(row.supplier_id), row);
    });
    return map;
  }, [tableData]);

  const preselectedSupplierIds = useMemo(() => {
    const initialIds = new Set<string>();
    if (Array.isArray(requestDetails?.selected_supplier_ids)) {
      requestDetails.selected_supplier_ids.forEach((id: any) => {
        if (id) {
          initialIds.add(String(id));
        }
      });
    }
    if (Array.isArray(requestDetails?.quotes_selections)) {
      requestDetails.quotes_selections.forEach((selection: any) => {
        const supplierId =
          selection?.supplier_id
            ? String(selection.supplier_id)
            : selection?.supplier?._id
              ? String(selection.supplier._id)
              : null;
        if (supplierId) {
          initialIds.add(supplierId);
        }
      });
    }
    return Array.from(initialIds);
  }, [requestDetails?.selected_supplier_ids, requestDetails?.quotes_selections]);

  useEffect(() => {
    initialSelectionsAppliedRef.current = false;
  }, [requestDetails]);

  const buildQuoteSelections = useCallback(
    (supplierIds: string[]) => {
      const aggregated: any[] = [];
      supplierIds.forEach((supplierId) => {
        const summaryRow = summaryRowMap.get(supplierId);
        if (!summaryRow) {
          return;
        }

        const existingSelection = existingSelectionsBySupplier.get(supplierId)?.[0] || {};
        const pendingUpdates = pendingFieldUpdatesRef.current[supplierId] || {};
        const leadTimeValue = normalizeFieldValue(
          pendingUpdates.lead_time ?? summaryRow.lead_time
        );
        const paymentTermsValue = normalizeFieldValue(
          pendingUpdates.payment_terms ?? summaryRow.payment_terms
        );
        const deliveryCostValue = normalizeFieldValue(
          pendingUpdates.delivery_cost ?? summaryRow.delivery_cost
        );
        const quoteValidUntilValue = normalizeFieldValue(
          pendingUpdates.quote_valid_until ?? summaryRow.quote_valid_until
        );
        const updatedSelection = {
          ...existingSelection,
          supplier_id: summaryRow.supplier_id,
          supplier_readable_id: summaryRow.supplier_readable_id,
          supplier_name: summaryRow.supplier_name,
          total_price: summaryRow.total_price,
          currency: summaryRow.currency,
          lead_time: leadTimeValue,
          payment_terms: paymentTermsValue,
          delivery_cost: deliveryCostValue,
          quote_valid_until: quoteValidUntilValue,
          source: existingSelection.source || 'quotes_review',
          last_updated: new Date().toISOString()
        };

        aggregated.push(updatedSelection);
      });

      return aggregated;
    },
    [existingSelectionsBySupplier, summaryRowMap]
  );

  const saveSelectionsToBackend = useCallback(
    async (selections: any[], supplierIds?: string[]) => {
      if (!token || !requestId) {
        throw new Error('Missing authentication context');
      }

      const payload: any = {
        quotes_selections: selections
      };

      if (supplierIds) {
        payload.selected_supplier_ids = supplierIds;
      }

      const response = await fetch(API_ENDPOINTS.REQUEST_V2_DETAIL(requestId), {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const result = await response.json();
      if (!response.ok || !result?.success) {
        throw new Error(result?.error || 'Failed to save selections');
      }

      setRequestDetails((prev: any) =>
        prev
          ? {
            ...prev,
            ...(supplierIds ? { selected_supplier_ids: supplierIds } : {}),
            quotes_selections: selections
          }
          : prev
      );
      setQuoteSelections(selections);
    },
    [token, requestId]
  );

  useEffect(() => {
    if (!tableData.length || initialSelectionsAppliedRef.current) {
      return;
    }

    if (!preselectedSupplierIds.length) {
      initialSelectionsAppliedRef.current = true;
      return;
    }

    const availablePreselected = preselectedSupplierIds.filter((id) => summaryRowMap.has(id));
    setSelectedSupplierIds(availablePreselected);
    setQuoteSelections(buildQuoteSelections(availablePreselected));
    initialSelectionsAppliedRef.current = true;
  }, [tableData.length, preselectedSupplierIds, summaryRowMap, buildQuoteSelections]);

  const handleEditToggle = useCallback(() => {
    setShowSummaryPage(false);
    setIsEditing((prev) => {
      const next = !prev;
      if (next) {
        setSaveError(null);
        setSaveSuccess(null);
      }
      return next;
    });
  }, []);

  const handleFieldEdit = useCallback(
    (supplierId: string, field: string, value: any, _row?: any) => {
      if (!EDITABLE_FIELDS_SET.has(field as EditableQuoteField)) {
        return;
      }

      const supplierKey = String(supplierId);
      const nextValue = normalizeFieldValue(value);

      pendingFieldUpdatesRef.current = {
        ...pendingFieldUpdatesRef.current,
        [supplierKey]: {
          ...(pendingFieldUpdatesRef.current[supplierKey] || {}),
          [field as EditableQuoteField]: nextValue
        }
      };

      setSummaryRows((prev) =>
        prev.map((row) =>
          String(row.supplier_id) === supplierKey
            ? ({ ...row, [field]: nextValue } as SupplierQuotesSummaryRow)
            : row
        )
      );
    },
    []
  );

  const handleSaveChanges = useCallback(() => {
    const supplierIds = [...selectedSupplierIds];
    const updatedSelections = buildQuoteSelections(supplierIds);

    setIsSaving(true);
    setSaveError(null);
    setSaveSuccess(null);

    void saveSelectionsToBackend(updatedSelections, supplierIds)
      .then(() => {
        pendingFieldUpdatesRef.current = {};
        setSaveSuccess('Selections saved successfully.');
      })
      .catch((err: any) => {
        console.error('Failed to save quote selections:', err);
        setSaveError(err?.message || 'Failed to save selections.');
        setIsEditing(true);
      })
      .finally(() => {
        setIsSaving(false);
      });
  }, [buildQuoteSelections, saveSelectionsToBackend, selectedSupplierIds]);

  const handleSelectionChange = useCallback(
    (ids: string[]) => {
      const uniqueIds = Array.from(new Set(ids));
      setSelectedSupplierIds(uniqueIds);
      setQuoteSelections(buildQuoteSelections(uniqueIds));
      setSaveSuccess(null);
      setSaveError(null);
      pendingFieldUpdatesRef.current = {};
      setShowSummaryPage(false);
    },
    [buildQuoteSelections]
  );

  const handleNext = useCallback(async () => {
    const supplierIds = [...selectedSupplierIds];
    const selections = buildQuoteSelections(supplierIds);
    setIsSaving(true);
    setSaveError(null);
    setSaveSuccess(null);

    try {
      await saveSelectionsToBackend(selections, supplierIds);
      pendingFieldUpdatesRef.current = {};
      setSaveSuccess('Selections saved successfully.');
      setShowSummaryPage(true);
    } catch (err: any) {
      console.error('Failed to save quote selections:', err);
      setSaveError(err?.message || 'Failed to save selections.');
    } finally {
      setIsSaving(false);
    }
  }, [buildQuoteSelections, saveSelectionsToBackend, selectedSupplierIds]);

  const handleNextClick = useCallback(() => {
    void handleNext();
  }, [handleNext]);

  const headerActionButtons = useMemo(
    () => [],
    []
  );

  const headerFeedback = useMemo(() => {
    if (finishError) {
      return (
        <div className="quotes-review-feedback quotes-review-feedback--error">
          {finishError}
        </div>
      );
    }
    if (saveError) {
      return (
        <div className="quotes-review-feedback quotes-review-feedback--error">
          {saveError}
        </div>
      );
    }
    if (finishSuccess) {
      return (
        <div className="quotes-review-feedback quotes-review-feedback--success">
          {finishSuccess}
        </div>
      );
    }
    if (saveSuccess) {
      return (
        <div className="quotes-review-feedback quotes-review-feedback--success">
          {saveSuccess}
        </div>
      );
    }
    return null;
  }, [finishError, saveError, finishSuccess, saveSuccess]);

  const selectedSummaryRows = useMemo(() => {
    return selectedSupplierIds
      .map((id) => summaryRowMap.get(id))
      .filter((row): row is QuotesSummaryRowView => !!row);
  }, [selectedSupplierIds, summaryRowMap]);

  const summaryMetrics = useMemo(
    () => [
      {
        key: 'total_price_display',
        label: 'Total Price',
        render: (row: QuotesSummaryRowView) => row.total_price_display || '—'
      },
      {
        key: 'lead_time',
        label: 'Lead Time',
        render: (row: QuotesSummaryRowView) => row.lead_time || '—'
      },
      {
        key: 'payment_terms',
        label: 'Payment Terms',
        render: (row: QuotesSummaryRowView) => row.payment_terms || '—'
      },
      {
        key: 'delivery_cost',
        label: 'Delivery Cost',
        render: (row: QuotesSummaryRowView) => row.delivery_cost || '—'
      },
      {
        key: 'quote_valid_until',
        label: 'Quote Valid Until',
        render: (row: QuotesSummaryRowView) => row.quote_valid_until || '—'
      },
      {
        key: 'overall_score_display',
        label: 'Overall Evaluation',
        render: (row: QuotesSummaryRowView) =>
          row.overall_score_display ? `${row.overall_score_display}` : '—'
      },
      {
        key: 'quote_link',
        label: 'Quote',
        render: (row: QuotesSummaryRowView) => (
          <button
            onClick={() => {
              setAttachmentSupplier({
                id: row.supplier_id,
                name: row.supplier_name
              });
              setShowAttachmentsModal(true);
            }}
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              color: '#2563eb',
              textDecoration: 'underline',
              cursor: 'pointer',
              fontFamily: 'inherit',
              fontSize: 'inherit'
            }}
          >
            {`View Attachments ${typeof attachmentCounts[row.supplier_id] === 'number' ? `(${attachmentCounts[row.supplier_id]})` : ''}`}
          </button>
        )
      }
    ],
    []
  );

  const handleBackToReview = useCallback(() => {
    setShowSummaryPage(false);
  }, []);

  const handleFinishRequest = useCallback(async () => {
    if (!requestId || !token) {
      return;
    }

    setIsFinishing(true);
    setFinishError(null);
    setFinishSuccess(null);

    try {
      // 1. Commit drafts first
      const commitResponse = await fetch(`${API_ENDPOINTS.BASE_URL}/supplier-products/commit-drafts`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ request_id: requestId })
      });

      const commitResult = await commitResponse.json();
      if (!commitResponse.ok || !commitResult.success) {
        // We might want to warn but continue, or block. 
        // For now, let's block to ensure data consistency.
        throw new Error(commitResult.error || 'Failed to commit product drafts');
      }

      // 2. Update Request Phase
      const response = await fetch(API_ENDPOINTS.REQUEST_PHASE_UPDATE(requestId), {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          phase: 'select_supplier',
          status: 'select_supplier',
          notes: 'Marked as select_supplier (final) from Quotes Review'
        })
      });

      const result = await response.json();
      if (!response.ok || !result?.success) {
        throw new Error(result?.error || 'Failed to finish request');
      }

      setRequestDetails((prev: any) =>
        prev
          ? {
            ...prev,
            phase: 'select_supplier',
            status: 'select_supplier'
          }
          : prev
      );
      setFinishSuccess('Request finalized successfully!');
      setShowFinishSuccessModal(true);
    } catch (err: any) {
      console.error('Failed to finish request:', err);
      setFinishError(err?.message || 'Failed to finish request.');
    } finally {
      setIsFinishing(false);
    }
  }, [requestId, token]);

  useEffect(() => {
    if (requestDetails?.phase === 'select_supplier' && selectedSupplierIds.length > 0) {
      setShowSummaryPage(true);
    }
  }, [requestDetails?.phase, selectedSupplierIds.length]);

  // Render product details in expanded row
  const renderExpandedContent = useCallback((row: QuotesSummaryRowView) => {
    const products = (row as any).products || [];
    if (products.length === 0) {
      return <div style={{ padding: '16px', color: '#6b7280' }}>No product details available</div>;
    }

    return (
      <div style={{
        padding: '16px 24px',
        backgroundColor: '#f9fafb',
        borderTop: '1px solid #e5e7eb'
      }}>
        <table style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: '14px'
        }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
              <th style={{ padding: '8px', textAlign: 'left', fontWeight: 600 }}>Product</th>
              <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600 }}>Quantity</th>
              <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600 }}>Unit Price</th>
              <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600 }}>Total</th>
            </tr>
          </thead>
          <tbody>
            {products.map((product: any, idx: number) => (
              <tr key={idx} style={{ borderBottom: '1px solid #e5e7eb' }}>
                <td style={{ padding: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {!product.has_price && (
                      <span title="No price provided for this product" style={{ color: '#f59e0b', fontSize: '16px' }}>⚠️</span>
                    )}
                    <span>{product.product_name}</span>
                  </div>
                </td>
                <td style={{ padding: '8px', textAlign: 'right' }}>{product.quantity}</td>
                <td style={{ padding: '8px', textAlign: 'right' }}>
                  {product.unit_price !== null ? formatNumber(product.unit_price, product.currency) : '—'}
                </td>
                <td style={{ padding: '8px', textAlign: 'right' }}>
                  {product.total_price !== null ? formatNumber(product.total_price, product.currency) : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }, []);

  if (showSummaryPage) {
    return (
      <>
        <UserLayout
          pageTitle="Quotes Summary"
          user={user}
          currentRoute={currentRoute}
          onNavigate={onNavigate}
          onLogout={onLogout}
          navigationActions={
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', width: '100%', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <button
                  onClick={handleBackToReview}
                  style={{
                    padding: '8px 16px',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    backgroundColor: '#ffffff',
                    color: '#374151',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                >
                  ← Back to Quotes Review
                </button>
              </div>
            </div>
          }
          headerActions={
            requestDetails?.phase !== 'select_supplier' && (
              <button
                className="quotes-summary-finish-button"
                onClick={handleFinishRequest}
                disabled={isFinishing}
              >
                {isFinishing ? 'Finishing...' : 'Finish Request'}
              </button>
            )
          }
        >
          <div className="quotes-review-summary">
            {headerFeedback}
            {selectedSummaryRows.length === 0 ? (
              <div className="quotes-summary-empty">
                No suppliers selected. Please go back and select suppliers to compare.
              </div>
            ) : (
              <div className="quotes-summary-table-wrapper">
                <table className="quotes-summary-table">
                  <thead>
                    <tr>
                      <th className="metric-column">Metric</th>
                      {selectedSummaryRows.map((row) => (
                        <th key={row.supplier_id}>
                          <div className="quotes-summary-supplier">
                            <span className="supplier-name">{row.supplier_name || 'Supplier'}</span>
                            {row.supplier_readable_id && (
                              <span className="supplier-id">{row.supplier_readable_id}</span>
                            )}
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {summaryMetrics.map((metric) => (
                      <tr key={metric.key}>
                        <td className="metric-label">{metric.label}</td>
                        {selectedSummaryRows.map((row) => (
                          <td key={`${row.supplier_id}-${metric.key}`}>{metric.render(row)}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </UserLayout>

        {/* Finish Request Success Modal */}
        <SuccessModal
          isOpen={showFinishSuccessModal}
          onClose={() => {
            setShowFinishSuccessModal(false);
            onNavigate('/dashboard/user/inbox/open');
          }}
          message={finishSuccess || 'Request finalized successfully!'}
          duration={3}
        />
        <RfqLockedModal
          isOpen={showRfqLockModal}
          lockInfo={rfqLockInfo}
          onClose={() => setShowRfqLockModal(false)}
          onGoBack={() => {
            setShowRfqLockModal(false);
            onNavigate('/dashboard/user/inbox');
          }}
        />
        {attachmentSupplier && requestId && (
          <QuoteAttachmentsModal
            isOpen={showAttachmentsModal}
            onClose={() => {
              setShowAttachmentsModal(false);
              setAttachmentSupplier(null);
            }}
            requestId={requestId}
            supplierId={attachmentSupplier.id}
            supplierName={attachmentSupplier.name}
          />
        )}
      </>
    );
  }

  return (
    <>
      <DataDashboard
        title="Quotes Review"
        pageDescription="Review and compare supplier quotes and pricing"
        collection="quotes_summary"
        data={tableData}
        columns={columns}
        isLoading={isLoading}
        error={error}
        user={user}
        currentRoute={currentRoute}
        onNavigate={onNavigate}
        onLogout={onLogout}
        onDataChange={() => { }}
        onEdit={handleFieldEdit}
        onEditToggle={handleEditToggle}
        onSaveChanges={handleSaveChanges}
        isEditing={isEditing}
        showAddButton={false}
        showSearch={false}
        showSelection={true}
        showDeleteButton={false}
        layoutType="user"
        getDynamicSubItems={getDynamicSubItems}
        headerActions={headerActionButtons}
        selectedItems={selectedSupplierIds}
        selectedItemsCount={selectedSupplierIds.length}
        onSelectionChange={handleSelectionChange}
        headerContent={headerFeedback}
        expandableRows={true}
        expandedRows={expandedRows}
        onRowExpand={toggleRowExpanded}
        renderExpandedContent={renderExpandedContent}
        navigationActions={(() => {
          const navInfo = getRFQNavigationInfo(currentRoute);
          if (!navInfo) return undefined;

          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', width: '100%', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                {navInfo.previousPage && (
                  <button
                    onClick={() => onNavigate(getRFQPageRoute(navInfo.baseRoute, navInfo.requestId, navInfo.previousPage!))}
                    style={{
                      padding: '8px 16px',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      backgroundColor: '#ffffff',
                      color: '#374151',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    ← Back to {getRFQPageName(navInfo.previousPage)}
                  </button>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <button
                  onClick={handleNextClick}
                  disabled={isSaving || selectedSupplierIds.length === 0 || isEditing}
                  style={{
                    padding: '8px 16px',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    backgroundColor: isSaving || selectedSupplierIds.length === 0 || isEditing ? '#f3f4f6' : '#ffffff',
                    color: isSaving || selectedSupplierIds.length === 0 || isEditing ? '#9ca3af' : '#374151',
                    cursor: isSaving || selectedSupplierIds.length === 0 || isEditing ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                >
                  {isSaving ? 'Saving...' : 'Next Quotes Review'} →
                </button>
              </div>
            </div>
          );
        })()}


      />
      <RfqLockedModal
        isOpen={showRfqLockModal}
        lockInfo={rfqLockInfo}
        onClose={() => setShowRfqLockModal(false)}
        onGoBack={() => {
          setShowRfqLockModal(false);
          onNavigate('/dashboard/user/inbox');
        }}
      />
      {attachmentSupplier && requestId && (
        <QuoteAttachmentsModal
          isOpen={showAttachmentsModal}
          onClose={() => {
            setShowAttachmentsModal(false);
            setAttachmentSupplier(null);
          }}
          requestId={requestId}
          supplierId={attachmentSupplier.id}
          supplierName={attachmentSupplier.name}
        />
      )}
    </>
  );
};

export default UserRFQQuotesReviewDashboard;

